import { useState, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';
import { showToast } from '@/components/rc';

const SILENCE_TIMEOUT_MS = 1800;
// Android's on-device recognizer applies its own end-of-speech detection and
// stops itself on silence — see the 'listeningState' handling below. Its
// final transcript lands via a trailing 'partialResults' event shortly after
// (the plugin's Java source routes onResults() through the same listener
// when partialResults is on), so give it a moment before reading the
// transcript. Same grace period covers a manual stop, where the recognizer
// is still actively listening when we tell it to end.
const NATIVE_FINALISE_GRACE_MS = 400;
// Backstop for the failure paths the plugin surfaces via neither event: the
// Android RecognitionListener's onError() (e.g. ERROR_SPEECH_TIMEOUT — the
// user taps the mic and never speaks, or ERROR_NETWORK/ERROR_AUDIO mid-
// utterance) never calls notifyListeners(), and since start() already
// resolved instantly for partialResults, there is nothing left for JS to
// reject either. Without this, those paths leave the UI on "listening"
// forever with zero signal. Reset on every partial result so a long,
// slow dictation isn't cut off; it only fires if the recognizer goes
// completely silent — no partial, no stop — for this long.
const NATIVE_WATCHDOG_MS = 8000;
const IS_NATIVE = Capacitor.isNativePlatform();

export function useRemyVoice(sendMessage: (text: string, imageBase64?: string, isVoice?: boolean) => Promise<void>) {
  const [isRecording, setIsRecording] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');
  const recognitionRef = useRef<any>(null);
  const partialResultsListenerRef = useRef<PluginListenerHandle | null>(null);
  const listeningStateListenerRef = useRef<PluginListenerHandle | null>(null);
  const watchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasSubmittedRef = useRef(false);
  const liveTranscriptRef = useRef('');
  const finalizedTextRef = useRef('');

  // Reached from two triggers: the user tapping stop mid-utterance, and
  // Android's recognizer stopping itself after silence (the audible tone the
  // user hears when they stop talking is the recognizer service's own UI,
  // not ours — see the 'listeningState' listener in startRecording). Guarded
  // by hasSubmittedRef so whichever trigger fires first wins.
  const finalizeNative = useCallback(async () => {
    if (hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;
    if (watchdogTimerRef.current) { clearTimeout(watchdogTimerRef.current); watchdogTimerRef.current = null; }

    // Harmless no-op if the recognizer already stopped itself (the plugin's
    // stop() only acts `if (listening)`). The native stop() never calls
    // call.resolve(), so awaiting it hangs forever.
    void SpeechRecognition.stop().catch(() => {});

    await new Promise(resolve => setTimeout(resolve, NATIVE_FINALISE_GRACE_MS));

    if (partialResultsListenerRef.current) { partialResultsListenerRef.current.remove(); partialResultsListenerRef.current = null; }
    if (listeningStateListenerRef.current) { listeningStateListenerRef.current.remove(); listeningStateListenerRef.current = null; }

    setIsRecording(false);
    const text = liveTranscriptRef.current.trim();
    if (text) sendMessage(text, undefined, true);
    setLiveTranscript('');
    liveTranscriptRef.current = '';
  }, [sendMessage]);

  // (Re)starts the watchdog countdown — called once recording begins and
  // again on every partial result, so it only ever fires from true silence.
  const armWatchdog = useCallback(() => {
    if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current);
    watchdogTimerRef.current = setTimeout(() => { finalizeNative(); }, NATIVE_WATCHDOG_MS);
  }, [finalizeNative]);

  const startRecording = useCallback(async () => {
    hasSubmittedRef.current = false;
    liveTranscriptRef.current = '';
    finalizedTextRef.current = '';
    setLiveTranscript('');

    if (IS_NATIVE) {
      try {
        // Reachable on real hardware since the manifest declares the
        // microphone optional — a device without one can install the app, and
        // tapping the mic here must explain itself rather than do nothing.
        const { available } = await SpeechRecognition.available();
        if (!available) {
          showToast({
            tone: 'neutral',
            message: "Voice input isn't available on this device — type your question instead.",
          });
          return;
        }

        const permissions = await SpeechRecognition.requestPermissions();
        if (permissions.speechRecognition !== 'granted') {
          showToast({
            tone: 'neutral',
            message: 'Microphone access is off. Enable it in Settings to talk to Remy.',
          });
          return;
        }

        // partialResults: true → start() resolves immediately (no hang).
        partialResultsListenerRef.current = await SpeechRecognition.addListener('partialResults', (data: { matches: string[] }) => {
          const transcript = data.matches?.[0] ?? '';
          if (!transcript) return;
          liveTranscriptRef.current = transcript;
          setLiveTranscript(transcript);
          armWatchdog();
        });

        // The recognizer applies its own silence-based end-of-speech
        // detection regardless of push-to-talk, and stops listening on its
        // own. Without this, the UI never learns that happened — it keeps
        // showing "listening" until the user taps stop, even though the mic
        // already went dead and the transcript is sitting there unsent.
        listeningStateListenerRef.current = await SpeechRecognition.addListener('listeningState', (data: { status: 'started' | 'stopped' }) => {
          if (data.status === 'stopped') finalizeNative();
        });

        setIsRecording(true);
        await SpeechRecognition.start({
          language: 'en-US',
          maxResults: 1,
          partialResults: true,
          popup: false,
        });
        armWatchdog();
      } catch (err: unknown) {
        console.error('[Voice] start error:', err);
        setIsRecording(false);
        setLiveTranscript('');
        liveTranscriptRef.current = '';
        if (partialResultsListenerRef.current) { partialResultsListenerRef.current.remove(); partialResultsListenerRef.current = null; }
        if (listeningStateListenerRef.current) { listeningStateListenerRef.current.remove(); listeningStateListenerRef.current = null; }
        if (watchdogTimerRef.current) { clearTimeout(watchdogTimerRef.current); watchdogTimerRef.current = null; }
      }
      return;
    }

    // ── Web (browser) ────────────────────────────────────────────────────────
    const SpeechRecognitionAPI = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) return;

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    const finalizeAndSubmitWeb = () => {
      if (hasSubmittedRef.current) return;
      hasSubmittedRef.current = true;
      if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
      if (recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; }
      setIsRecording(false);
      const finalText = liveTranscriptRef.current.trim();
      if (finalText) sendMessage(finalText, undefined, true);
      setLiveTranscript('');
      liveTranscriptRef.current = '';
    };

    recognition.onresult = (event: any) => {
      const result = event.results[event.resultIndex];
      if (result.isFinal) finalizedTextRef.current += result[0].transcript;
      const fullTranscript = finalizedTextRef.current + (result.isFinal ? '' : result[0].transcript);
      liveTranscriptRef.current = fullTranscript;
      setLiveTranscript(fullTranscript);
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(finalizeAndSubmitWeb, SILENCE_TIMEOUT_MS);
    };

    recognition.onend = () => {
      if (!hasSubmittedRef.current) {
        const text = liveTranscriptRef.current.trim();
        if (text) { finalizeAndSubmitWeb(); } else { setIsRecording(false); setLiveTranscript(''); liveTranscriptRef.current = ''; }
      }
    };

    recognition.onerror = (event: any) => {
      console.error('[Voice] Web error:', event.error);
      setIsRecording(false);
      setLiveTranscript('');
      liveTranscriptRef.current = '';
    };

    recognition.start();
    recognitionRef.current = recognition;
    setIsRecording(true);
  }, [sendMessage, finalizeNative, armWatchdog]);

  const stopRecording = useCallback(async () => {
    if (IS_NATIVE) {
      await finalizeNative();
      return;
    }

    // Web path
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
    const currentText = liveTranscriptRef.current.trim();
    hasSubmittedRef.current = true;
    if (recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; }
    setIsRecording(false);
    if (currentText) sendMessage(currentText, undefined, true);
    setLiveTranscript('');
    liveTranscriptRef.current = '';
  }, [finalizeNative, sendMessage]);

  return { isRecording, liveTranscript, startRecording, stopRecording };
}
